import React, { useState, useRef } from 'react';
import {
  Upload, FileText, CheckCircle2, X,
  ChevronRight, Info, Table as TableIcon, Download,
  Settings, Check, AlertTriangle, ArrowRight, MousePointer2
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/ui/table";
import API from '@/api/api01';
import { toast } from 'sonner';
import { cn } from '@/ui/utils';

interface CSVImportGeneralLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess: () => void;
}

type ImportStep = 'upload' | 'mapping' | 'review';

export default function CSVImportGeneralLeadModal({ open, onOpenChange, onImportSuccess }: CSVImportGeneralLeadModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [editingCell, setEditingCell] = useState<{ row: number, field: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const EXPECTED_FIELDS = [
    { key: 'name', label: 'Student Name', required: true },
    { key: 'email', label: 'Email', required: true },
    { key: 'phone', label: 'Phone Number', required: false },
    { key: 'course', label: 'Applied Course', required: false },
    { key: 'source', label: 'Lead Source', required: false },
    { key: 'priority', label: 'Priority', required: false },
    { key: 'notes', label: 'Notes', required: false },
  ];

  const [mapping, setMapping] = useState<Record<string, string>>({});

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setIsParsing(false);
    setIsImporting(false);
    setSkipDuplicates(true);
    setEditingCell(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  React.useEffect(() => {
    if (!open) {
      const timer = setTimeout(resetState, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  };

  const processFile = (f: File) => {
    if (f.type !== 'text/csv' && !f.name.endsWith('.csv')) {
      toast.error("Please upload a valid CSV file");
      return;
    }
    setFile(f);
    parseCSV(f);
  };

  const parseCSV = (f: File) => {
    setIsParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
        if (lines.length < 2) {
          toast.error("CSV file is empty or missing data");
          setIsParsing(false);
          setFile(null);
          return;
        }
        const csvHeaders = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        setHeaders(csvHeaders);
        const data = lines.slice(1).map(line => {
          const values: string[] = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') inQuotes = !inQuotes;
            else if (char === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
            else current += char;
          }
          values.push(current.trim());
          const obj: any = {};
          csvHeaders.forEach((header, index) => { obj[header] = values[index]?.replace(/^"|"$/g, '') || ''; });
          return obj;
        });
        setCsvData(data);
        const newMapping: Record<string, string> = {};
        EXPECTED_FIELDS.forEach(field => {
          const match = csvHeaders.find(h =>
            h.toLowerCase() === field.key.toLowerCase() ||
            h.toLowerCase() === field.label.toLowerCase() ||
            (field.key === 'name' && h.toLowerCase().includes('name')) ||
            (field.key === 'phone' && h.toLowerCase().includes('phone')) ||
            (field.key === 'email' && h.toLowerCase().includes('email'))
          );
          if (match) newMapping[field.key] = match;
        });
        setMapping(newMapping);
        setStep('mapping');
      } catch (err) {
        toast.error("Error parsing CSV file");
        setFile(null);
      } finally {
        setIsParsing(false);
      }
    };
    reader.readAsText(f);
  };

  const handleCellEdit = (rowIndex: number, fieldKey: string, value: string) => {
    setCsvData(prev => {
      const newData = [...prev];
      const csvHeader = mapping[fieldKey];
      if (csvHeader) newData[rowIndex] = { ...newData[rowIndex], [csvHeader]: value };
      return newData;
    });
  };

  const handleImport = async () => {
    const missingRequired = EXPECTED_FIELDS.filter(f => f.required && !mapping[f.key]);
    if (missingRequired.length > 0) {
      toast.error(`Please map required fields: ${missingRequired.map(f => f.label).join(', ')}`);
      return;
    }
    setIsImporting(true);
    try {
      const finalLeads = csvData.map(row => {
        const lead: any = {};
        EXPECTED_FIELDS.forEach(field => {
          const csvHeader = mapping[field.key];
          lead[field.key] = csvHeader ? row[csvHeader] : (field.key === 'priority' ? 'Medium' : (field.key === 'source' ? 'Website' : ''));
        });
        return lead;
      }).filter(l => l.name && l.email);
      const res = await API.post('/leads/bulk-import', { leads: finalLeads, skip_duplicates: skipDuplicates });
      toast.success(`Successfully imported ${res.data.count} leads${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
      onOpenChange(false);
      setTimeout(() => { onImportSuccess(); }, 1000);
    } catch (err) {
      toast.error("Failed to import leads");
    } finally {
      setIsImporting(false);
    }
  };

  const downloadSample = () => {
    const h = "Student Name,Email,Phone Number,Applied Course,Lead Source,Priority,Notes";
    const b = "Aryan Khan,aryan@example.com,9876543210,B.Tech CSE,Website,High,Good profile";
    const blob = new Blob([h + "\n" + b], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample_student_leads.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const STEPS = [
    { id: 'upload', icon: Upload, label: 'Upload' },
    { id: 'mapping', icon: Settings, label: 'Map' },
    { id: 'review', icon: CheckCircle2, label: 'Review' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px] h-[90vh] max-h-[720px] flex flex-col p-0 overflow-hidden bg-white dark:bg-card border border-slate-200 dark:border-slate-800 shadow-2xl rounded-2xl">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 rounded-t-2xl z-10" />

        {/* Header */}
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider mb-1">
                <Settings size={11} />
                LMS Import Processor v3.0
              </div>
              <DialogTitle className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                Import <span className="text-blue-600 dark:text-blue-400">Bulk Leads</span>
              </DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm">
                Scale your admissions pipeline with our high-end lead ingestion tool.
              </DialogDescription>
            </div>

            {/* Step indicators */}
            <div className="hidden md:flex items-center gap-2">
              {STEPS.map((s, idx) => (
                <React.Fragment key={s.id}>
                  <div className={cn(
                    "flex items-center justify-center h-9 w-9 rounded-xl transition-all duration-300 border",
                    step === s.id
                      ? "bg-blue-600 text-white border-blue-600 shadow-md scale-110"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700"
                  )}>
                    <s.icon size={16} />
                  </div>
                  {idx < 2 && <ArrowRight size={13} className="text-slate-300 dark:text-slate-600" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">

          {/* STEP: Upload */}
          {step === 'upload' && (
            <div className="animate-in fade-in zoom-in duration-300">
              <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); processFile(e.dataTransfer.files[0]); }}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "relative h-[360px] border-2 border-dashed rounded-2xl flex flex-col items-center justify-center p-10 transition-all duration-300 group cursor-pointer",
                  "border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500",
                  "bg-slate-50 dark:bg-slate-900/50 hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
                )}
              >
                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-blue-200 dark:text-blue-900">
                  <MousePointer2 size={100} />
                </div>

                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="h-20 w-20 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300">
                    <Upload className="h-9 w-9 text-blue-600 dark:text-blue-400 group-hover:animate-bounce" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2 tracking-tight">Drop Lead Records</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 max-w-xs">
                    Drag and drop your spreadsheet file here, or click to browse files.
                  </p>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" className="rounded-xl h-10 px-5 font-semibold border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">
                      Browse Files
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); downloadSample(); }}
                      className="rounded-xl h-10 px-5 font-semibold gap-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                    >
                      <Download size={15} />
                      Sample CSV
                    </Button>
                  </div>
                </div>

                <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleFileChange} />
              </div>

              <div className="mt-5 flex items-center justify-between px-1">
                <div className="flex gap-2">
                  {['Name *', 'Email *', 'Phone', 'Course', 'Source', 'Priority'].map(f => (
                    <Badge key={f} variant="outline" className="rounded-lg text-[10px] font-medium border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                      {f}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest">
                  <Check size={13} />
                  Secure Upload
                </div>
              </div>
            </div>
          )}

          {/* STEP: Mapping */}
          {step === 'mapping' && (
            <div className="animate-in slide-in-from-bottom-4 duration-300 space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="p-3.5 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-100 dark:border-blue-900 flex gap-2.5">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-bold text-blue-700 dark:text-blue-300">Map your columns</h4>
                      <p className="text-xs text-blue-600/70 dark:text-blue-400/60 mt-0.5">Automated detection done. Please verify the field mappings below.</p>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                    {EXPECTED_FIELDS.map(field => (
                      <div key={field.key} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-center justify-between hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                        <div className="flex flex-col">
                          <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 flex items-center gap-1 uppercase tracking-wider">
                            {field.label}
                            {field.required && <span className="text-red-500 text-sm leading-none">*</span>}
                          </label>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500">System field</span>
                        </div>
                        <div className="relative w-44">
                          <select
                            value={mapping[field.key] || ''}
                            onChange={(e) => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className={cn(
                              "w-full h-9 px-3 pr-8 rounded-lg appearance-none text-xs font-semibold transition-all border",
                              "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200",
                              "focus:outline-none focus:ring-2 focus:ring-blue-500/30",
                              mapping[field.key]
                                ? "border-blue-400 dark:border-blue-500"
                                : (field.required ? "border-red-400 dark:border-red-600" : "border-slate-300 dark:border-slate-600")
                            )}
                          >
                            <option value="">-- Ignore Field --</option>
                            {headers.map(h => (<option key={h} value={h}>{h}</option>))}
                          </select>
                          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                            {mapping[field.key]
                              ? <CheckCircle2 size={13} className="text-blue-600 dark:text-blue-400" />
                              : <Settings size={13} className="text-slate-400 dark:text-slate-500" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Preview */}
                <div className="space-y-3">
                  <div className="bg-slate-900 dark:bg-slate-950 p-5 rounded-2xl border border-slate-700 dark:border-slate-800 min-h-[400px] flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-bold text-sm flex items-center gap-2">
                        <MousePointer2 size={14} className="text-blue-400" />
                        Live Preview
                      </h4>
                      <Badge variant="outline" className="bg-white/5 border-white/10 text-white/50 font-normal text-[10px]">
                        {csvData.length} Records
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {csvData.slice(0, 5).map((row, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-white/5 border border-white/5">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-[9px] text-white/30 uppercase font-black tracking-widest">{mapping['name'] || 'Name'}</p>
                              <p className="text-xs font-bold text-white truncate">{row[mapping['name'] || headers[0]] || '—'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[9px] text-white/30 uppercase font-black tracking-widest">{mapping['course'] || 'Course'}</p>
                              <p className="text-xs font-bold text-blue-400 truncate">{row[mapping['course'] || headers[3]] || '—'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-auto pt-4 border-t border-white/5 text-center text-[9px] text-white/20 font-bold uppercase tracking-widest">
                      LMS Data Ingestion Stream
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Review */}
          {step === 'review' && (
            <div className="animate-in slide-in-from-right-4 duration-300 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-3">
                  <div className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-3 py-1.5 rounded-xl text-emerald-700 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                    <Check size={13} /> {csvData.length} records ready
                  </div>
                  <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-xl text-slate-500 dark:text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                    <MousePointer2 size={13} /> Click cell to edit
                  </div>
                </div>

                <div
                  className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer"
                  onClick={() => setSkipDuplicates(!skipDuplicates)}
                >
                  <div className={cn("w-7 h-3.5 rounded-full transition-colors relative", skipDuplicates ? "bg-blue-600" : "bg-slate-300 dark:bg-slate-600")}>
                    <div className={cn("absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full transition-all shadow-sm", skipDuplicates ? "translate-x-3.5" : "translate-x-0.5")} />
                  </div>
                  <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-tight">Skip Duplicates</span>
                </div>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-slate-900">
                <div className="max-h-[400px] overflow-auto">
                  <Table>
                    <TableHeader className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                      <TableRow className="border-slate-200 dark:border-slate-700 hover:bg-transparent">
                        {EXPECTED_FIELDS.filter(f => mapping[f.key]).map(field => (
                          <TableHead key={field.key} className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest py-3">
                            {field.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.map((row, rowIndex) => (
                        <TableRow key={rowIndex} className="border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 group transition-colors">
                          {EXPECTED_FIELDS.filter(f => mapping[f.key]).map(field => {
                            const csvHeader = mapping[field.key];
                            const value = row[csvHeader] || '';
                            const isEditing = editingCell?.row === rowIndex && editingCell?.field === field.key;
                            const isMissing = field.required && !value;
                            return (
                              <TableCell
                                key={field.key}
                                className={cn("py-2.5 text-xs font-medium transition-all cursor-pointer", isMissing ? "text-red-500" : "text-slate-700 dark:text-slate-300")}
                                onClick={() => setEditingCell({ row: rowIndex, field: field.key })}
                              >
                                {isEditing ? (
                                  <input
                                    autoFocus
                                    className="bg-blue-50 dark:bg-blue-950/40 border border-blue-400 dark:border-blue-500 rounded-lg px-2 py-1 w-full text-slate-900 dark:text-white outline-none focus:ring-2 ring-blue-500/30 text-xs"
                                    value={value}
                                    onChange={(e) => handleCellEdit(rowIndex, field.key, e.target.value)}
                                    onBlur={() => setEditingCell(null)}
                                    onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                                  />
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <span className="truncate max-w-[140px]">{value || <span className="text-red-400 italic">Required</span>}</span>
                                    <Badge className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-100 dark:bg-slate-700 text-[8px] text-slate-500 dark:text-slate-400 border-none px-1.5 py-0">
                                      Edit
                                    </Badge>
                                  </div>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-8 py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between gap-4">
          <div className="flex-1 flex items-center gap-2">
            {step === 'mapping' && (
              <div className={cn("flex items-center gap-1.5 text-xs font-semibold",
                EXPECTED_FIELDS.filter(f => f.required && !mapping[f.key]).length > 0
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-emerald-600 dark:text-emerald-400"
              )}>
                <AlertTriangle size={13} />
                {EXPECTED_FIELDS.filter(f => f.required && !mapping[f.key]).length > 0 ? "Map all required fields to continue" : "All required fields mapped — ready to review!"}
              </div>
            )}
            {step === 'review' && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
                <CheckCircle2 size={13} /> Data validated. Ready to import.
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => step === 'mapping' ? setStep('upload') : step === 'review' ? setStep('mapping') : onOpenChange(false)}
              className="rounded-xl h-10 px-6 font-semibold border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
            >
              {step === 'upload' ? 'Cancel' : 'Back'}
            </Button>

            <Button
              onClick={() => {
                if (step === 'upload') { if (file) parseCSV(file); }
                else if (step === 'mapping') { setStep('review'); }
                else { handleImport(); }
              }}
              disabled={step === 'upload' ? !file : (step === 'mapping' ? !mapping['name'] : isImporting)}
              className="rounded-xl h-10 px-8 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all duration-200 min-w-[160px] flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  {step === 'upload' ? 'Analyze File' : step === 'mapping' ? 'Review & Edit' : 'Import Leads'}
                  <ChevronRight size={16} />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
